import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import artistImageUrlGenerator from 'utils/artistImageUrlGenerator';
import EventTickets from 'components/Events/EventTickets';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import classnames from 'classnames';
import MuiLink from '@mui/material/Link';
import AdvancedFormat from 'dayjs/plugin/advancedFormat';

import AddToCalendar from 'components/AddToCalendar';
import { Box } from '@mui/material';

dayjs.extend(AdvancedFormat);

const classes = {
  card: {
    display: 'flex',
    flexDirection: 'row-reverse',
  },
  details: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  content: {
    flex: '1 0 auto',
  },
  cover: {
    width: '15%',
    position: 'relative',
    height: '100%',
  },
  acts: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  event: (theme) => ({
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(),
  }),
  eventicon: (theme) => ({
    color: theme.palette.primary.main,
    marginRight: theme.spacing(),
  }),
  text: {
    fontSize: '1.2em',
  },
  expand: (theme) => ({
    transform: 'rotate(0deg)',
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.shortest,
    }),
  }),
  expandOpen: {
    transform: 'rotate(180deg)',
  },
};

export default function EventItem({ event }) {
  const [showCalendar, setShowCalendar] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const {
    id,
    title,
    name,
    venue,
    artist,
    acts,
    date,
    pageid,
    city,
    country,
    attending,
    interested,
  } = event;

  const location = [name, city, country].filter(Boolean).join(', ') || title || venue;

  return (
    <Card
      data-event={id}
      sx={(theme) =>
        attending
          ? {
              marginTop: theme.spacing(2),
              boxShadow: '-17px -18px 10px -20px #00e5ff',
              background: 'linear-gradient(197deg, #00e5ff2b, transparent)',
            }
          : { marginTop: theme.spacing(2) }
      }
    >
      <Box sx={classes.card}>
        <CardMedia
          component="img"
          sx={classes.cover}
          src={artistImageUrlGenerator(pageid, 150)}
          title={`${artist} image`}
          loading="lazy"
          width="150"
          height="150"
        >
          {/* {attending && (
            <Typography
              sx={{
                bgcolor: 'secondary.main',
                textTransform: 'uppercase',
                position: 'absolute',
                transformOrigin: 'top right',
                transform: 'rotate(270deg) translateY(-100%)',
                width: '120%',
                textAlign: 'center',
                fontWeight: 700,
                color: 'black',
                right: 0,
              }}
            >
              Going
            </Typography>
          )} */}
        </CardMedia>
        <Box sx={classes.details}>
          <CardContent sx={classes.content}>
            <Box sx={classes.event}>
              <AddToCalendar iconOnly event={{ ...event, location }} />
              <Typography sx={classes.title} color="textSecondary">
                {dayjs(date).format('dddd Do MMMM YYYY')}
              </Typography>
              {(attending || interested) && (
                // <EventRibbon text={attending ? "You're going!" : 'On your wishlist'} />
                <Chip
                  sx={{ marginLeft: 'auto' }}
                  size="small"
                  label={attending ? "You're going!" : 'On your wishlist'}
                  color="secondary"
                  variant={attending ? 'filled' : 'outlined'}
                />
              )}
            </Box>
            <Typography component="h3" variant="h5" sx={classes.text}>
              {location}
            </Typography>
            <Typography
              data-test="acts"
              variant="subtitle1"
              color="textSecondary"
              sx={classes.acts}
              component="p"
            >
              <MuiLink component={Link} to={`/artist/${pageid}`}>
                {artist}
              </MuiLink>
            </Typography>
          </CardContent>
          <ButtonGroup
            fullWidth
            variant="text"
            color="primary"
            aria-label="event links and tickets"
          >
            <Button
              data-test="tickets"
              size="small"
              color="primary"
              aria-expanded={expanded}
              aria-label="Tickets"
              onClick={() => setExpanded(!expanded)}
            >
              Tickets
              <ExpandMoreIcon sx={{ ...classes.expand, ...(expanded ? classes.expandOpen : {}) }} />
            </Button>
            <Button
              data-test="moreinfo"
              sx={classes.action}
              component={Link}
              to={`/concert/${event.eventPageId || event.id}`}
            >
              More Details
            </Button>
          </ButtonGroup>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <CardContent>
              <Divider />
              <EventTickets eventId={id} />
            </CardContent>
          </Collapse>
        </Box>
        {/* <AttendingButtons /> */}
        {/* 1. Add "Want to go?" to tickets */}
        {/* 2. Add AttendingButtons to main details page */}
        {/* 3. Add "Wishlist" / "Going" banners over image */}
      </Box>
    </Card>
  );
}
